import {
  ExternalCollection,
  FetchParams,
} from "@bitwarden/web-vault/app/services/store/external.collection.abstraction";
import { ExternalView } from "@bitwarden/web-vault/app/models/view/external.view";
import { inject, signal, WritableSignal } from "@angular/core";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { IReferenceData } from "@bitwarden/web-vault/app/models/view/reference-data/reference-data.view";
import { capFetch } from "@bitwarden/web-vault/utils/fetch";
import { PluginConfigService, PluginConfig, PluginIdentifier } from "./plugin-config.service";

interface ReferencesSelector {
  symbol?: { code?: string; name?: string; currency?: string };
  date?: string;
  rate?: number;
}

interface FetchResponse<T> {
  nodes: T[];
  info?: { hasNext?: boolean; cursor?: string };
  total?: number;
}

interface FetchCurrencyParams extends FetchParams {
  type: GraphQLQuery;
  limit?: number;
  cursor?: string;
  symbol?: string;
  // ISO 8601 formatted date string e.g. 2022-01-01
  date?: string;
}

type GraphQLQuery = "symbols" | "references";

interface GraphQLResponse<T> {
  data: {
    [key in GraphQLQuery]: FetchResponse<T>;
  };
}

export type SymbolView = { code: string; name: string };

/**
 * Bounds on one fetch's pagination (#6392, as #6365 did for plugin transactions): a backend that
 * keeps answering hasNext fetched pages forever, with no delay, until the tab ran out of memory.
 * DATA_BANK_STUCK_PAGES pages in a row that bring only what it already has means the pagination
 * isn't moving. The page caps are per query: the symbols list can be long, but a references
 * lookup is one symbol on one date, and every page is a request against the plugin's API quota.
 */
export const DATA_BANK_MAX_SYMBOL_PAGES = 1000;
export const DATA_BANK_MAX_REFERENCE_PAGES = 50;
export const DATA_BANK_STUCK_PAGES = 3;

// A value as a GraphQL string literal (JSON's escaping is valid GraphQL), or null: a quote or
// backslash in a cursor, a typed-in symbol or an API key can't break the query. "" is a value
// like any other (a cursor may be "", #6393).
const graphQLString = (value?: string | null): string =>
  value == null ? "null" : JSON.stringify(value);

const symbolKey = (code: string) => code;
// One quote: a symbol's rate in a currency on a date, from a source.
const referenceKey = (reference: IReferenceData) =>
  JSON.stringify([reference.base, reference.symbol, reference.date.date, reference.source]);

// Null-safe: callers (e.g. add-symbol-form's save) can reach this before the databank config is
// loaded, and strictNullChecks is off, so nothing else flags it.
export const getSourceFromConfig = (config: PluginConfig | null): string => {
  return config?.name || PluginIdentifier.DATABANK;
};

export class ExternalDataBankStoreCollection extends ExternalCollection {
  readonly pluginName = PluginIdentifier.DATABANK;
  private log: LogService = inject(LogService);
  private pluginConfigService: PluginConfigService = inject(PluginConfigService);
  // TODO: will change it to SymbolView once it is abled to save into vault
  private _symbols = signal<string[]>([]);
  readonly symbols = this._symbols.asReadonly();
  private _references = signal<IReferenceData[]>([]);
  readonly references = this._references.asReadonly();
  private _enabled = signal<boolean>(false);
  readonly enabled = this._enabled.asReadonly();
  private token: string | null = null;
  readonly config = signal(this.pluginConfigService.getConfig(this.pluginName));
  // Bumped whenever the config or session changes (triggerChanges, clear). Work started under an
  // older generation is dropped when it lands: a stale auth mustn't overwrite the current key's
  // token, and a search that outlives a logout mustn't fill the next session's results.
  private generation = 0;
  // The auth triggerChanges() started; searches wait for it so they don't go out with no token
  // (or the previous key's) right after a key is saved.
  private authReady: Promise<void> = Promise.resolve();

  clear() {
    this.generation++;
    this.token = null;
    this.authReady = Promise.resolve();
    this._symbols.set([]);
    this._references.set([]);
    this._enabled.set(false);
    this.config.set(null);
  }

  async fetch(params: FetchCurrencyParams) {
    // find the data bank config
    const config = this.config();

    if (!config || config.enabled !== true) {
      return [];
    }

    const generation = this.generation;
    await this.authReady;
    if (generation !== this.generation) {
      return [];
    }

    this._enabled.set(config.enabled);

    if (params.type === "symbols") {
      await this.fetchSymbols(config.url, generation, params.limit || 20, params.cursor);
      return this.symbols();
    } else if (params.type === "references") {
      const { symbol, date } = params;
      // The source comes from the config this fetch started with, not this.config(), which
      // clear() or triggerChanges() may replace while a request is in flight.
      await this.fetchReferences(
        config.url,
        generation,
        { symbol, date },
        getSourceFromConfig(config),
        params.limit || 20,
        params.cursor,
      );
      return this.references();
    }
  }

  triggerChanges(): void {
    // TODO: we can add search api instead of fetching all the symbols
    // Refresh config from service in case it was updated
    this.config.set(this.pluginConfigService.getConfig(this.pluginName));
    const config = this.config();
    // A changed (or removed) key's token must not outlive it, even if the new key's auth fails.
    const generation = ++this.generation;
    this.token = null;

    if (!config || config.enabled !== true) {
      this.authReady = Promise.resolve();
      return;
    }

    this.authReady = this.auth(config.url, config.apiKey)
      .then((res) => {
        if (generation === this.generation) {
          this.token = res.data.auth;
        }
      })
      // Also called after the user saves an EODHD key (plugin-details-dialog), so a bad key must
      // not surface as an unhandled rejection.
      .catch((error) => this.log.error(`Error authenticating with the data bank: ${error}`));
  }

  protected mapToView(data: any): ExternalView {
    throw new Error("Method not implemented.");
  }

  private async fetchSymbols(url: string, generation: number, limit: number, cursor?: string) {
    await this.fetchPages(
      "symbols",
      DATA_BANK_MAX_SYMBOL_PAGES,
      generation,
      cursor,
      this._symbols,
      symbolKey,
      async (pageCursor) => {
        const { data } = (await this.query(
          url,
          `
            query {
              symbols(option: { limit: ${limit}, cursor: ${graphQLString(pageCursor)} }) {
                nodes { code name }
                info { hasNext cursor }
              }
            }
          `,
        )) as GraphQLResponse<SymbolView>;
        const { info, nodes } = data.symbols;
        return { info, items: nodes.map((symbol) => symbol.code) };
      },
    );
  }

  private async fetchReferences(
    url: string,
    generation: number,
    args: { symbol: string; date: string },
    source: string,
    limit: number,
    cursor?: string,
  ) {
    // No symbol or date (undefined or "") is "any", as before.
    const symbol = graphQLString(args.symbol || null);
    const date = graphQLString(args.date || null);
    await this.fetchPages(
      "references",
      DATA_BANK_MAX_REFERENCE_PAGES,
      generation,
      cursor,
      this._references,
      referenceKey,
      async (pageCursor) => {
        const { data } = (await this.query(
          url,
          `
            query {
              references(symbol: ${symbol} startDate: ${date} endDate: ${date} option: { limit: ${limit}, cursor: ${graphQLString(pageCursor)} }) {
                nodes { rate, date, symbol { code, name, currency } }
                info { hasNext cursor }
                total
              }
            }
          `,
        )) as GraphQLResponse<ReferencesSelector>;
        const { info, nodes } = data.references;
        // TODO: add reference data source type from the plugin config
        const items = nodes.map<IReferenceData>((reference) => ({
          symbol: reference.symbol!.currency!,
          date: { date: reference.date, timeZone: "GMT", time: null, reliableTimeSource: false },
          rate: reference.rate,
          base: reference.symbol.code,
          source,
        }));
        return { info, items };
      },
    );
  }

  /**
   * Every page from `cursor` on, merged into `target` as it comes (by key: a later, changed copy
   * replaces the one there, in its place). Bounded (#6392): it gives up past `maxPages` pages, or
   * after DATA_BANK_STUCK_PAGES pages in a row that bring only what it already fetched.
   * - Progress is judged by the items, never the cursor: a backend may page by a server-side
   *   session and send the same cursor back. A changed copy (e.g. a corrected rate) is progress.
   * - A page with no cursor (none, or "") is the last, whatever its hasNext says, as before #6392:
   *   there's nowhere to go next, and sending the last cursor again (as plugin sync does) would
   *   bring a stateless backend's same page back, against the plugin's API quota.
   * - An empty page is neither progress nor a repeat (a backend may walk empty ranges), unless its
   *   cursor didn't move either: then nothing at all moved. The page cap bounds the rest.
   * - Giving up, like a failed request, is logged and keeps the pages already merged: callers start
   *   a fetch() without awaiting it and read the signal, so a rejection would go unhandled, and
   *   those pages still answer lookups.
   * - Once clear() or triggerChanges() starts a new generation, it merges and logs nothing more.
   */
  private async fetchPages<T>(
    name: GraphQLQuery,
    maxPages: number,
    generation: number,
    cursor: string | undefined,
    target: WritableSignal<T[]>,
    keyOf: (item: T) => string,
    fetchPage: (
      cursor: string | undefined,
    ) => Promise<{ info?: FetchResponse<T>["info"]; items: T[] }>,
  ): Promise<void> {
    // By key, the copy last fetched (as JSON, to tell a changed copy from a repeat).
    const fetched = new Map<string, string>();
    let repeats = 0;
    try {
      for (let page = 0; ; page++) {
        if (page === maxPages) {
          throw new Error(`pagination exceeded ${maxPages} pages`);
        }
        const { info, items } = await fetchPage(cursor);
        if (generation !== this.generation) {
          return;
        }
        // The page's copy of each key is its last, as merged: two copies of a key in one page
        // mustn't each look new against the other on every repeat of that page.
        const copies = new Map(items.map((item) => [keyOf(item), JSON.stringify(item)]));
        let progress = 0;
        for (const [key, copy] of copies) {
          if (fetched.get(key) !== copy) {
            fetched.set(key, copy);
            progress++;
          }
        }
        const nextCursor = info?.cursor;
        if (items.length) {
          repeats = progress === 0 ? repeats + 1 : 0;
        } else if (nextCursor === cursor) {
          repeats++;
        }
        // Only a new or changed item gives the signal's readers a new array (or a new object):
        // mergeByKey returns the same array otherwise, and set() ignores an identical value.
        target.set(mergeByKey(target(), items, keyOf));
        if (!info?.hasNext || !nextCursor) {
          break;
        }
        if (repeats === DATA_BANK_STUCK_PAGES) {
          throw new Error(
            `pagination is stuck: ${DATA_BANK_STUCK_PAGES} pages in a row brought nothing new`,
          );
        }
        cursor = nextCursor;
      }
    } catch (error) {
      if (generation === this.generation) {
        this.log.error(`Error fetching ${name}: ${error}`);
      }
    }
  }

  private async auth(url: string, apiKey?: string) {
    const q = `
      query { auth(apiKey: ${graphQLString(apiKey)}) }
    `;
    return await this.query(url, q);
  }

  private async query(url: string, q: string) {
    const headers: { "Content-Type": string; Authorization?: string } = {
      "Content-Type": "application/json",
    };

    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
    }

    const options = {
      url,
      method: "POST",
      headers,
      data: JSON.stringify({ query: q }),
    };

    const response = await capFetch(options);

    if (response.status !== 200) {
      throw new Error("Network response was not ok");
    }

    return response.data;
  }
}

// `list` with each item in it: one with a key already there replaces that one, in its place, if it
// differs; others are appended in order. `list` itself if nothing is new or changed, so a quote
// fetched again keeps its object (the forms' effects read it, and would apply it again).
function mergeByKey<T>(list: T[], items: T[], keyOf: (item: T) => string): T[] {
  const merged = [...list];
  const indexOf = new Map(merged.map((item, index) => [keyOf(item), index]));
  let changed = false;
  for (const item of items) {
    const key = keyOf(item);
    const index = indexOf.get(key);
    if (index === undefined) {
      indexOf.set(key, merged.length);
      merged.push(item);
      changed = true;
    } else if (JSON.stringify(merged[index]) !== JSON.stringify(item)) {
      merged[index] = item;
      changed = true;
    }
  }
  return changed ? merged : list;
}
